import { useEffect, useMemo, useRef, useState } from "react";
import { useGame } from "@/game/GameContext";
import { applySupplyBonus, pickRandomEvent, SCENARIOS } from "@/game/gameData";
import {
  calcFinalScore,
  pickStrongestWeakest,
  pickTitle,
  recap,
  statLabel,
} from "@/game/gameLogic";
import { sfx, startMonthMusic, stopMusic, isMusicEnabled } from "@/game/audioEngine";
import HUD from "@/components/HUD";
import ProgressTrail from "@/components/ProgressTrail";
import PixelButton from "@/components/PixelButton";
import { ASH_MEDIA } from "@/game/media";
import { toast } from "sonner";

// Phases:
//   intro    → see-choices button
//   choosing → list of choices
//   outcome  → outcome of selected choice
//   income   → (month >= 4) monthly sales notification
//   event    → random event (~chance)
//   (then NEXT_MONTH or END)
export default function MonthScreen() {
  const { state, dispatch } = useGame();
  const scenario = useMemo(
    () => state.scenarios[state.month - 1] || SCENARIOS[0],
    [state.month, state.scenarios]
  );

  const [phase, setPhase] = useState("intro");
  const [lastOutcome, setLastOutcome] = useState(null);
  const [eventResult, setEventResult] = useState(null);
  const [incomeAmount, setIncomeAmount] = useState(0);
  const [abilityArmed, setAbilityArmed] = useState(false);
  const lastMusicMonth = useRef(0);

  // Per-month music (different loop each month)
  useEffect(() => {
    if (lastMusicMonth.current !== state.month) {
      if (isMusicEnabled()) startMonthMusic(state.month);
      lastMusicMonth.current = state.month;
    }
  }, [state.month]);

  useEffect(() => {
    return () => stopMusic();
  }, []);

  const bootstrap = state.bootstrap;

  const handleChoice = (choice, idx) => {
    if (bootstrap && choice.cost > 0) {
      sfx.zap();
      toast.error("Locked in Bootstrap Mode — paid choices are off the wagon.");
      return;
    }
    if (choice.cost > state.stats.budget) {
      sfx.zap();
      toast.error("Not enough budget for that choice.");
      return;
    }

    let deltas = { ...(choice.deltas || {}) };
    if (choice.cost > 0) deltas = { ...deltas, budget: -choice.cost };

    const bonus = applySupplyBonus(choice, state.supplies);
    Object.keys(bonus).forEach((k) => {
      deltas[k] = (deltas[k] || 0) + bonus[k];
    });

    if (abilityArmed && state.character) {
      const aid = state.character.id;
      if (aid === "scrappy_founder") {
        Object.keys(deltas).forEach((k) => {
          if (deltas[k] < 0) deltas[k] = Math.ceil(deltas[k] / 2);
        });
      } else if (aid === "brand_wrangler") {
        Object.keys(deltas).forEach((k) => {
          if (k !== "budget" && deltas[k] > 0) deltas[k] += 4;
        });
      } else if (aid === "content_ranger") {
        deltas.awareness = (deltas.awareness || 0) + 10;
        deltas.energy = Math.max(deltas.energy || 0, 0);
      } else if (aid === "seo_prospector") {
        deltas.leads = (deltas.leads || 0) + 5;
        deltas.systems = (deltas.systems || 0) + 3;
      } else if (aid === "referral_scout") {
        deltas.leads = (deltas.leads || 0) + 8;
      }
      dispatch({ type: "USE_ABILITY" });
      setAbilityArmed(false);
      sfx.ability();
    }

    dispatch({ type: "APPLY_DELTAS", deltas });
    sfx.select();

    const entry = {
      month: scenario.month,
      month_title: scenario.title,
      choice_index: idx,
      choice_label: choice.label,
      outcome: choice.outcome,
      stat_deltas: deltas,
    };
    dispatch({ type: "LOG_CHOICE", entry });
    setLastOutcome({ choice, deltas, lesson: scenario.lesson });
    setPhase("outcome");
  };

  // Income rules:
  //   • Months 1–5: no income
  //   • Months 6–12: stat-driven income, $25 increments, $0–$275 normally
  //   • Months 9–12: ONE specific pre-rolled month yields a "big" payout
  //                   $300–$500 ($25 increments). Other months in this range
  //                   still roll the normal $0–$275 path.
  const proceedAfterOutcome = () => {
    if (state.month < 6) {
      maybeEventOrNext();
      return;
    }
    const s = state.stats;
    const avg = (s.awareness + s.trust + s.leads + s.energy + s.systems) / 5;

    const isBigMonth =
      state.month >= 9 &&
      state.month === state.bigIncomeMonth &&
      !state.bigIncomeFired;

    let amount;
    if (isBigMonth) {
      // Big payout: 9 buckets ($300, $325, ..., $500)
      const baseBucket = (avg / 100) * 8; // 0–8
      const variance = (Math.random() - 0.5) * 2; // ±1 bucket
      const bucket = Math.max(0, Math.min(8, Math.round(baseBucket + variance)));
      amount = 300 + bucket * 25;
      dispatch({ type: "FIRE_BIG_INCOME" });
    } else {
      // Normal payout: 12 buckets ($0, $25, ..., $275)
      const baseBucket = (avg / 100) * 11; // 0–11
      const variance = (Math.random() - 0.5) * 4; // ±2 buckets
      const bucket = Math.max(0, Math.min(11, Math.round(baseBucket + variance)));
      amount = bucket * 25;
    }

    setIncomeAmount(amount);
    if (amount > 0) {
      dispatch({ type: "APPLY_DELTAS", deltas: { budget: amount } });
      sfx.cash();
    } else {
      sfx.zap();
    }
    setPhase("income");
  };

  const maybeEventOrNext = () => {
    const chance = state.month < 3 ? 0.25 : 0.55;
    if (Math.random() < chance) {
      const ev = pickRandomEvent();
      const evDeltas = ev.apply(state.stats, state.supplies);
      setEventResult({ event: ev, deltas: evDeltas });
      dispatch({ type: "APPLY_DELTAS", deltas: evDeltas });
      sfx.zap();
      setPhase("event");
    } else {
      goToNextMonth();
    }
  };

  const goToNextMonth = () => {
    if (state.month >= 12) {
      const stats = state.stats;
      const startingBudget = state.wheelOutcome?.budget || 0;
      const score = calcFinalScore(stats, startingBudget);
      const { strongest, weakest } = pickStrongestWeakest(stats);
      const title = pickTitle({ score, stats, bootstrap: state.bootstrapTriggered });
      const result = {
        score,
        title,
        strongest: statLabel(strongest),
        weakest: statLabel(weakest),
        recap: recap({
          stats,
          bootstrap: state.bootstrapTriggered,
          businessName: state.player.businessName,
        }),
      };
      dispatch({ type: "SET_RESULT", result });
      sfx.win();
      stopMusic();
      dispatch({ type: "GOTO", screen: "end" });
    } else {
      dispatch({ type: "NEXT_MONTH" });
      setLastOutcome(null);
      setEventResult(null);
      setIncomeAmount(0);
      setPhase("intro");
    }
  };

  const startChoosing = () => {
    sfx.click();
    setPhase("choosing");
  };

  const tryAbility = () => {
    if (state.abilityUsed) {
      toast("Ability already used this game.");
      return;
    }
    setAbilityArmed(true);
    sfx.ding();
    toast.success(`${state.character?.ability.name} armed — applies to your next choice.`);
  };

  const anim = scenario.anim;

  return (
    <div className="min-h-screen px-3 md:px-4 py-4 md:py-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="pixel-font text-[10px] text-white/80">
            MONTH {state.month} / 12 — {scenario.title.toUpperCase()}
          </div>
          <ProgressTrail month={state.month} />
        </div>

        <div className="flex flex-col-reverse gap-4 lg:grid lg:grid-cols-[300px_1fr]">
          <HUD stats={state.stats} month={state.month} bootstrap={bootstrap} />

          <div className="space-y-4">
            {bootstrap && (
              <div className="pixel-panel" data-testid="bootstrap-banner">
                <div className="pixel-font text-[11px] text-red-700 mb-1">BOOTSTRAP MODE</div>
                <p className="text-sm">
                  Your budget wagon is empty, but the trail continues. Time to bootstrap, barter and
                  believe in spreadsheets. Paid choices are locked.
                </p>
              </div>
            )}

            <div className="pixel-panel" data-testid="month-panel">
              <div className="flex items-start gap-3 mb-3">
                <img src={ASH_MEDIA.campfire} alt="" className="w-16 h-16 pixelated border-4 border-black hidden sm:block" />
                <div className="flex-1">
                  <div className="pixel-font text-[9px] md:text-[10px] text-[#F58634] tracking-widest mb-1">
                    MONTH {state.month} OF 12
                  </div>
                  <div className="pixel-font text-[10px] text-[#1A1A1A]/60">FOCUS</div>
                  <div className="pixel-font text-sm md:text-base text-[#F58634]">{scenario.focus}</div>
                  <h3 className="pixel-font text-base md:text-lg mt-2">{scenario.title}</h3>
                </div>
                {anim && (
                  <div
                    aria-hidden="true"
                    className={`text-4xl md:text-5xl select-none ${anim.className}`}
                    data-testid={`month-anim-${state.month}`}
                  >
                    {anim.emoji}
                  </div>
                )}
              </div>

              <p className="text-base leading-relaxed mb-3" data-testid="month-narrative">
                {scenario.narrative({
                  playerName: state.player.playerName,
                  businessName: state.player.businessName,
                  audience: state.player.audience || "your audience",
                })}
              </p>

              {phase === "intro" && (
                <div className="flex flex-col sm:flex-row gap-2 mt-4">
                  <PixelButton data-testid="see-choices-button" onClick={startChoosing}>
                    See Choices ▶
                  </PixelButton>
                  {state.character && !state.abilityUsed && (
                    <PixelButton
                      data-testid="use-ability-button"
                      variant="ghost"
                      onClick={tryAbility}
                    >
                      ★ {state.character.ability.name}
                    </PixelButton>
                  )}
                </div>
              )}

              {phase === "choosing" && (
                <div className="space-y-2 mt-3" data-testid="choice-list">
                  {scenario.choices.map((c, i) => {
                    const locked = bootstrap && c.cost > 0;
                    const unaffordable = !locked && c.cost > state.stats.budget;
                    return (
                      <button
                        type="button"
                        key={i}
                        data-testid={`choice-${i}`}
                        disabled={locked || unaffordable}
                        onClick={() => handleChoice(c, i)}
                        className="choice-card w-full"
                      >
                        <div className="choice-letter">{String.fromCharCode(65 + i)}</div>
                        <div className="flex-1">
                          <div className="font-semibold text-sm md:text-base">{c.label}</div>
                          <div className="text-xs text-[#1A1A1A]/70 mt-0.5">
                            {c.cost > 0 ? `Cost: $${c.cost}` : "Free move"}
                            {locked && " — Locked (Bootstrap)"}
                            {!locked && unaffordable && " — Not enough budget"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {abilityArmed && (
                    <div className="pixel-font text-[10px] text-[#F58634] pixel-blink mt-2">
                      ★ {state.character?.ability.name} armed
                    </div>
                  )}
                </div>
              )}

              {phase === "outcome" && lastOutcome && (
                <div className="mt-3 space-y-3" data-testid="month-outcome">
                  <div className="bg-[#0F172A] text-white border-4 border-black p-3">
                    <div className="pixel-font text-[10px] text-[#F58634] mb-1">OUTCOME</div>
                    <p className="text-sm md:text-base">{lastOutcome.choice.outcome}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(lastOutcome.deltas).map(([k, v]) =>
                      v === 0 ? null : (
                        <span
                          key={k}
                          className={`pixel-font text-[10px] px-2 py-1 border-2 border-black ${
                            v > 0 ? "bg-emerald-200" : "bg-red-200"
                          }`}
                        >
                          {statLabel(k)} {v > 0 ? `+${v}` : v}
                        </span>
                      )
                    )}
                  </div>
                  <div className="bg-orange-100 border-4 border-black p-3">
                    <div className="pixel-font text-[10px] text-[#F58634] mb-1">LESSON</div>
                    <p className="text-sm">{lastOutcome.lesson}</p>
                  </div>
                  <div className="flex justify-end">
                    <PixelButton
                      data-testid="continue-button"
                      onClick={proceedAfterOutcome}
                    >
                      Continue ▶
                    </PixelButton>
                  </div>
                </div>
              )}

              {phase === "income" && (
                <div className="mt-3 space-y-3 anim-income-slide" data-testid="income-panel">
                  <div
                    className={`border-4 border-black p-4 flex items-center gap-4 ${
                      incomeAmount > 0 ? "bg-emerald-100" : "bg-slate-200"
                    }`}
                  >
                    <div className={`text-4xl ${incomeAmount > 0 ? "anim-coin-pop" : "anim-pulse-slow"}`}>
                      {incomeAmount > 0 ? "💰" : "🪙"}
                    </div>
                    <div className="flex-1">
                      <div className={`pixel-font text-[10px] mb-1 ${incomeAmount > 0 ? "text-emerald-800" : "text-slate-700"}`}>
                        SALES THIS MONTH
                      </div>
                      {incomeAmount > 0 ? (
                        <>
                          <div className="pixel-font text-base md:text-lg">
                            You earned <span className="text-emerald-700">+${incomeAmount.toLocaleString()}</span>
                          </div>
                          <div className="text-sm text-[#1A1A1A]/70 mt-1">
                            Real revenue from your work this month landed in the wagon.
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="pixel-font text-base md:text-lg">
                            A quiet month: <span className="text-slate-700">+$0</span>
                          </div>
                          <div className="text-sm text-[#1A1A1A]/70 mt-1">
                            Awareness, trust and leads were too low to convert. Strengthen them and the trail will start paying.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <PixelButton data-testid="income-continue" onClick={maybeEventOrNext}>
                      Continue ▶
                    </PixelButton>
                  </div>
                </div>
              )}

              {phase === "event" && eventResult && (
                <div className="mt-3 space-y-3" data-testid="event-panel">
                  <div className="bg-purple-100 border-4 border-black p-3">
                    <div className="pixel-font text-[10px] text-[#F58634] mb-1">⚡ RANDOM EVENT</div>
                    <div className="pixel-font text-sm">{eventResult.event.title}</div>
                    <p className="text-sm mt-2">{eventResult.event.message}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(eventResult.deltas).map(([k, v]) =>
                      v === 0 ? null : (
                        <span
                          key={k}
                          className={`pixel-font text-[10px] px-2 py-1 border-2 border-black ${
                            v > 0 ? "bg-emerald-200" : "bg-red-200"
                          }`}
                        >
                          {statLabel(k)} {v > 0 ? `+${v}` : v}
                        </span>
                      )
                    )}
                  </div>
                  <div className="flex justify-end">
                    <PixelButton data-testid="event-continue" onClick={goToNextMonth}>
                      {state.month >= 12 ? "Finish The Trail ▶" : "Next Month ▶"}
                    </PixelButton>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
